import { FormEvent, useState } from 'react';
import { useTenantCurrent, useUpdateBranding } from '../lib/hooks';
import { Button, Field, PageHeader, useSavedFlag, useToast } from '../components/ui';

export function BrandingPage() {
  const tenant = useTenantCurrent();
  const updateBranding = useUpdateBranding();
  const { toast } = useToast();
  const [saved, flashSaved] = useSavedFlag();
  const [preview, setPreview] = useState<{ brandColor?: string; accentColor?: string; portalTitle?: string }>({});

  const branding = tenant.data?.branding;
  const previewBrand = preview.brandColor || branding?.brandColor || '#0b8f67';
  const previewAccent = preview.accentColor || branding?.accentColor || '#142019';
  const previewTitle = preview.portalTitle ?? (branding?.portalTitle || 'Your Portal Name');

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const form = new FormData(event.currentTarget);
    try {
      await updateBranding.mutateAsync({
        logoUrl: String(form.get('logoUrl') || ''),
        brandColor: String(form.get('brandColor') || ''),
        accentColor: String(form.get('accentColor') || ''),
        portalTitle: String(form.get('portalTitle') || ''),
        supportEmail: String(form.get('supportEmail') || ''),
        customCss: String(form.get('customCss') || ''),
      });
      flashSaved();
      toast({
        title: 'Branding saved',
        description: 'Your colors and title now apply across the whole portal for everyone on your team.',
      });
    } catch (error) {
      toast({ variant: 'error', title: 'Could not save branding', description: (error as Error).message });
    }
  }

  return (
    <>
      <PageHeader
        title="Branding"
        description="Make this portal yours. Your clients and team will see your name, logo, and colors — not ours."
      />

      <div className="grid" style={{ gridTemplateColumns: 'minmax(0, 1fr) 360px' }}>
        <section className="card">
          <form className="grid" onSubmit={onSubmit}>
            <div className="form-grid">
              <Field label="Portal name" hint="Shown in the sidebar and the browser tab.">
                <input
                  name="portalTitle"
                  defaultValue={branding?.portalTitle || ''}
                  placeholder="Bright Local Dashboard"
                  onChange={(event) => setPreview((current) => ({ ...current, portalTitle: event.target.value }))}
                />
              </Field>
              <Field label="Support email" hint="Where the “Questions?” button sends your users.">
                <input name="supportEmail" type="email" defaultValue={branding?.supportEmail || ''} placeholder="help@youragency.com" />
              </Field>
              <Field label="Logo URL" hint="A square image works best (at least 80×80 px).">
                <input name="logoUrl" defaultValue={branding?.logoUrl || ''} placeholder="https://…/logo.png" />
              </Field>
              <Field label="Primary color" hint="Used for buttons, highlights, and accents.">
                <input
                  name="brandColor"
                  type="color"
                  defaultValue={branding?.brandColor || '#0b8f67'}
                  onChange={(event) => setPreview((current) => ({ ...current, brandColor: event.target.value }))}
                />
              </Field>
              <Field label="Accent color" hint="A darker companion color for depth.">
                <input
                  name="accentColor"
                  type="color"
                  defaultValue={branding?.accentColor || '#142019'}
                  onChange={(event) => setPreview((current) => ({ ...current, accentColor: event.target.value }))}
                />
              </Field>
            </div>

            <Field
              label="Custom CSS (advanced)"
              hint="Optional. For designers who want fine-grained control over the portal’s look."
            >
              <textarea name="customCss" rows={7} defaultValue={branding?.customCss || ''} className="mono" spellCheck={false} />
            </Field>

            <div>
              <Button type="submit" loading={updateBranding.isPending} loadingText="Saving…" saved={saved} savedText="Saved">
                Save branding
              </Button>
            </div>
          </form>
        </section>

        <section className="card">
          <h3>Live preview</h3>
          <p className="card-sub">How your header will feel with these colors.</p>
          <div
            style={{
              borderRadius: 14,
              background: `linear-gradient(135deg, ${previewBrand} 0%, ${previewAccent} 100%)`,
              color: '#fff',
              padding: 20,
              transition: 'background 0.3s ease',
            }}
          >
            <div className="inline" style={{ gap: 12 }}>
              {branding?.logoUrl ? (
                <img
                  src={branding.logoUrl}
                  alt="Logo preview"
                  style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 10, border: '1px solid rgba(255,255,255,0.3)' }}
                />
              ) : (
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 10,
                    display: 'grid',
                    placeItems: 'center',
                    background: 'rgba(255,255,255,0.16)',
                    fontWeight: 700,
                  }}
                >
                  {(previewTitle || 'P').charAt(0).toUpperCase()}
                </div>
              )}
              <div>
                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700 }}>{previewTitle || 'Your Portal Name'}</div>
                <div style={{ fontSize: '0.78rem', opacity: 0.85 }}>Client-facing portal</div>
              </div>
            </div>
            <div
              style={{
                marginTop: 16,
                background: 'rgba(255,255,255,0.95)',
                color: previewAccent,
                borderRadius: 9,
                padding: '8px 12px',
                fontSize: '0.82rem',
                fontWeight: 650,
                display: 'inline-block',
              }}
            >
              Sample button
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
